// Cliente da API. A sessão vai no cookie httpOnly; nenhum token de integração passa pelo navegador.
import type {
  ActivityDTO,
  AgentDTO,
  BoardDTO,
  CommentDTO,
  ExecutionDTO,
  LabelDTO,
  Page,
  ProjectDTO,
  ReleaseDTO,
  TemplateDTO,
  AttachmentDTO,
  SavedViewDTO,
  AgentReport,
  SprintDTO,
  SprintDetailDTO,
  SprintVelocityDTO,
  NotificationDTO,
  NotificationPrefsDTO,
  TaskDTO,
  TaskSummaryDTO,
  UserDTO,
  TimeReport,
  TimerDTO,
  ValidationDTO,
  WorklogDTO,
  TaskArchiveResult,
  DocumentDTO,
  DocumentSummaryDTO,
} from '@jynexus/shared';

export class ApiError extends Error {
  status: number;
  code: string;
  details?: unknown;
  requestId?: string;
  constructor(status: number, code: string, message: string, details?: unknown, requestId?: string) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
    this.requestId = requestId;
  }
}

async function request<T>(method: string, url: string, body?: unknown, headers: Record<string, string> = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      credentials: 'same-origin',
      headers: { ...(body instanceof Blob ? { 'content-type': 'application/octet-stream' } : body !== undefined ? { 'content-type': 'application/json' } : {}), 'x-nexus-client': 'web', ...headers },
      body: body instanceof Blob ? body : body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError(0, 'network_error', 'Sem conexão com o servidor do Nexus. Nada foi salvo.');
  }
  const text = await res.text();
  const data = text ? JSON.parse(text) : undefined;
  if (!res.ok) {
    const e = data?.error ?? {};
    throw new ApiError(res.status, e.code ?? 'error', e.message ?? `erro ${res.status}`, e.details, e.requestId);
  }
  return data as T;
}

const qs = (params: Record<string, unknown>) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '' || (Array.isArray(v) && !v.length)) continue;
    p.set(k, Array.isArray(v) ? v.join(',') : String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
};

export type Me = { kind: 'user'; id: string; name: string; email: string; role: 'admin' | 'member' };

export interface Dashboard {
  period: { from: string; to: string };
  totals: Record<'open' | 'done' | 'canceled' | 'blocked' | 'overdue' | 'dueSoon' | 'createdInPeriod' | 'completedInPeriod' | 'runningExecutions' | 'loggedMinutesInPeriod' | 'openEstimateMinutes' | 'openSpentMinutes', number>;
  byStatus: Record<string, number>;
  byPriority: Record<string, number>;
  byLabel: { name: string; color: string; count: number }[];
  projects: { key: string; name: string; status: string; priority: string; open: number; done: number; blocked: number; total: number; percentDone: number; loggedMinutesInPeriod: number }[];
  throughput: { date: string; completed: number }[];
  priorityTasks: TaskSummaryDTO[];
  blockedTasks: TaskSummaryDTO[];
  overdueTasks: TaskSummaryDTO[];
  dueSoonTasks: TaskSummaryDTO[];
  recentActivity: ActivityDTO[];
  recentExecutions: ExecutionDTO[];
}

export const api = {
  me: () => request<Me>('GET', '/api/v1/auth/me'),
  login: (email: string, password: string) => request<{ user: UserDTO }>('POST', '/api/v1/auth/login', { email, password }),
  logout: () => request('POST', '/api/v1/auth/logout'),
  changePassword: (currentPassword: string, newPassword: string) => request('POST', '/api/v1/auth/password', { currentPassword, newPassword }),

  dashboard: (f: Record<string, unknown>) => request<Dashboard>('GET', `/api/v1/dashboard${qs(f)}`),

  projects: (includeArchived = false) => request<ProjectDTO[]>('GET', `/api/v1/projects${qs({ includeArchived })}`),
  project: (key: string) => request<ProjectDTO>('GET', `/api/v1/projects/${key}`),
  createProject: (body: unknown) => request<ProjectDTO>('POST', '/api/v1/projects', body),
  updateProject: (key: string, body: unknown) => request<ProjectDTO>('PATCH', `/api/v1/projects/${key}`, body),

  tasks: (f: Record<string, unknown>) => request<Page<TaskSummaryDTO>>('GET', `/api/v1/tasks${qs(f)}`),
  task: (key: string) => request<TaskDTO>('GET', `/api/v1/tasks/${key}`),
  createTask: (body: unknown) => request<TaskDTO>('POST', '/api/v1/tasks', body),
  updateTask: (key: string, body: unknown) => request<TaskDTO>('PATCH', `/api/v1/tasks/${key}`, body),
  transition: (key: string, body: unknown) => request<{ task: TaskDTO }>('POST', `/api/v1/tasks/${key}/transition`, body),
  move: (key: string, body: unknown) => request<{ task: TaskDTO }>('POST', `/api/v1/tasks/${key}/move`, body),
  completionCheck: (key: string) => request<{ canComplete: boolean; problems: string[] }>('GET', `/api/v1/tasks/${key}/completion-check`),
  archiveTask: (key: string, reason: string) => request('DELETE', `/api/v1/tasks/${key}${qs({ reason })}`),
  /** Arquivar card (sai do board) — diferente de excluir. */
  shelveTasks: (keys: string[]) => request<TaskArchiveResult>('POST', '/api/v1/tasks/archive', { keys }),
  unshelveTasks: (keys: string[]) => request<TaskArchiveResult>('POST', '/api/v1/tasks/unarchive', { keys }),
  taskLabels: (key: string, body: { add?: string[]; remove?: string[] }) => request<TaskDTO>('POST', `/api/v1/tasks/${key}/labels`, body),
  link: (key: string, body: { type: string; target: string }) => request<TaskDTO>('POST', `/api/v1/tasks/${key}/links`, body),
  unlink: (key: string, body: { type: string; target: string }) => request<TaskDTO>('POST', `/api/v1/tasks/${key}/links/remove`, body),
  comments: (key: string) => request<CommentDTO[]>('GET', `/api/v1/tasks/${key}/comments`),
  addComment: (key: string, body: { body: string; kind?: string; parentId?: string }) => request<CommentDTO>('POST', `/api/v1/tasks/${key}/comments`, body),
  validations: (key: string) => request<ValidationDTO[]>('GET', `/api/v1/tasks/${key}/validations`),
  addValidation: (key: string, body: unknown) => request<ValidationDTO>('POST', `/api/v1/tasks/${key}/validations`, body),
  reportBlocker: (key: string, body: { reason: string; blockedByKey?: string }) => request<TaskDTO>('POST', `/api/v1/tasks/${key}/blockers`, body),
  resolveBlocker: (key: string, id: string, resolution: string) => request<TaskDTO>('POST', `/api/v1/tasks/${key}/blockers/${id}/resolve`, { resolution }),
  taskActivity: (key: string) => request<ActivityDTO[]>('GET', `/api/v1/tasks/${key}/activity`),
  worklogs: (key: string) => request<WorklogDTO[]>('GET', `/api/v1/tasks/${key}/worklogs`),
  addWorklog: (key: string, body: { minutes: number; note?: string; startedAt?: string }) => request<WorklogDTO>('POST', `/api/v1/tasks/${key}/worklogs`, body),
  deleteWorklog: (key: string, id: string) => request('DELETE', `/api/v1/tasks/${key}/worklogs/${id}`),
  timeReport: (f: Record<string, unknown>) => request<TimeReport>('GET', `/api/v1/reports/time${qs(f)}`),
  myTimer: () => request<{ timer: TimerDTO | null }>('GET', '/api/v1/timer'),
  startTimer: (key: string) => request<{ timer: TimerDTO; stopped: WorklogDTO | null }>('POST', `/api/v1/tasks/${key}/timer/start`),
  stopTimer: (note?: string) => request<{ worklog: WorklogDTO | null }>('POST', '/api/v1/timer/stop', { note }),
  cancelTimer: () => request<{ canceled: boolean }>('POST', '/api/v1/timer/cancel'),
  releases: (f: Record<string, unknown>) => request<ReleaseDTO[]>('GET', `/api/v1/releases${qs(f)}`),
  release: (id: string) => request<ReleaseDTO & { tasks: TaskSummaryDTO[] }>('GET', `/api/v1/releases/${id}`),
  createRelease: (body: unknown) => request<ReleaseDTO>('POST', '/api/v1/releases', body),
  updateRelease: (id: string, body: unknown) => request<ReleaseDTO>('PATCH', `/api/v1/releases/${id}`, body),
  releaseNotes: (id: string) => request<{ notes: string }>('POST', `/api/v1/releases/${id}/notes`),
  publishRelease: (id: string, body: unknown) => request<ReleaseDTO>('POST', `/api/v1/releases/${id}/publish`, body),
  notifications: () => request<{ items: NotificationDTO[]; unread: number }>('GET', '/api/v1/notifications'),
  readNotifications: (body: { ids?: string[]; all?: boolean }) => request<{ unread: number }>('POST', '/api/v1/notifications/read', body),
  notificationPrefs: () => request<NotificationPrefsDTO>('GET', '/api/v1/notifications/prefs'),
  updateNotificationPrefs: (body: unknown) => request<NotificationPrefsDTO>('PUT', '/api/v1/notifications/prefs', body),
  testNotificationEmail: () => request<{ sent: boolean; to?: string; reason?: string }>('POST', '/api/v1/notifications/test'),
  sprints: (f: Record<string, unknown>) => request<SprintDTO[]>('GET', `/api/v1/sprints${qs(f)}`),
  sprint: (id: string) => request<SprintDetailDTO>('GET', `/api/v1/sprints/${id}`),
  createSprint: (body: unknown) => request<SprintDTO>('POST', '/api/v1/sprints', body),
  updateSprint: (id: string, body: unknown) => request<SprintDTO>('PATCH', `/api/v1/sprints/${id}`, body),
  startSprint: (id: string) => request<SprintDTO>('POST', `/api/v1/sprints/${id}/start`),
  closeSprint: (id: string, body: unknown) => request<SprintDTO>('POST', `/api/v1/sprints/${id}/close`, body),
  deleteSprint: (id: string) => request<{ ok: true }>('DELETE', `/api/v1/sprints/${id}`),
  velocity: (project: string) => request<SprintVelocityDTO>('GET', `/api/v1/sprints/velocity${qs({ project })}`),
  agentReport: (f: Record<string, unknown>) => request<AgentReport>('GET', `/api/v1/reports/agents${qs(f)}`),
  views: (page?: string) => request<SavedViewDTO[]>('GET', `/api/v1/views${qs({ page })}`),
  createView: (body: unknown) => request<SavedViewDTO>('POST', '/api/v1/views', body),
  deleteView: (id: string) => request('DELETE', `/api/v1/views/${id}`),
  attachments: (key: string) => request<AttachmentDTO[]>('GET', `/api/v1/tasks/${key}/attachments`),
  uploadAttachment: (key: string, file: Blob, filename: string) => request<AttachmentDTO>('POST', `/api/v1/tasks/${key}/attachments${qs({ filename })}`, file),
  deleteAttachment: (id: string) => request('DELETE', `/api/v1/attachments/${id}`),
  templates: (project?: string) => request<TemplateDTO[]>('GET', `/api/v1/templates${qs({ project })}`),
  createTemplate: (body: unknown) => request<TemplateDTO>('POST', '/api/v1/templates', body),
  updateTemplate: (id: string, body: unknown) => request<TemplateDTO>('PATCH', `/api/v1/templates/${id}`, body),
  archiveTemplate: (id: string) => request('DELETE', `/api/v1/templates/${id}`),
  documents: (project: string, q?: string) => request<DocumentSummaryDTO[]>('GET', `/api/v1/projects/${project}/documents${qs({ q })}`),
  document: (id: string) => request<DocumentDTO>('GET', `/api/v1/documents/${id}`),
  createDocument: (project: string, body: { title: string; content?: string; parentId?: string | null }) => request<DocumentDTO>('POST', `/api/v1/projects/${project}/documents`, body),
  updateDocument: (id: string, body: { title?: string; content?: string; parentId?: string | null; position?: number; expectedVersion?: number }) => request<DocumentDTO>('PATCH', `/api/v1/documents/${id}`, body),
  deleteDocument: (id: string) => request<{ deleted: number }>('DELETE', `/api/v1/documents/${id}`),
  executions: (f: Record<string, unknown>) => request<ExecutionDTO[]>('GET', `/api/v1/executions${qs(f)}`),

  activities: (f: Record<string, unknown>) => request<ActivityDTO[]>('GET', `/api/v1/activities${qs(f)}`),

  labels: (project?: string) => request<LabelDTO[]>('GET', `/api/v1/labels${qs({ project })}`),
  createLabel: (body: unknown) => request<LabelDTO>('POST', '/api/v1/labels', body),
  updateLabel: (id: string, body: unknown) => request<LabelDTO>('PATCH', `/api/v1/labels/${id}`, body),
  deleteLabel: (id: string) => request<{ removedFromTasks: number }>('DELETE', `/api/v1/labels/${id}`),

  board: (project?: string) => request<BoardDTO>('GET', `/api/v1/board${qs({ project })}`),
  updateBoard: (project: string | undefined, body: unknown) => request<BoardDTO>('PUT', `/api/v1/board${qs({ project })}`, body),
  resetBoard: (project: string) => request<BoardDTO>('DELETE', `/api/v1/board${qs({ project })}`),

  users: () => request<UserDTO[]>('GET', '/api/v1/users'),
  createUser: (body: unknown) => request<UserDTO>('POST', '/api/v1/users', body),
  updateUser: (id: string, body: unknown) => request<UserDTO>('PATCH', `/api/v1/users/${id}`, body),
  agents: () => request<AgentDTO[]>('GET', '/api/v1/agents'),
  createAgent: (body: unknown) => request<{ agent: AgentDTO; token: string }>('POST', '/api/v1/agents', body),
  updateAgent: (id: string, body: unknown) => request<AgentDTO>('PATCH', `/api/v1/agents/${id}`, body),
  rotateAgentToken: (id: string) => request<{ agent: AgentDTO; token: string }>('POST', `/api/v1/agents/${id}/rotate-token`),
};

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    const d = e.details as { problems?: string[]; candidates?: unknown[] } | undefined;
    if (d?.problems?.length) return `${e.message}:\n• ${d.problems.join('\n• ')}`;
    return e.message;
  }
  return e instanceof Error ? e.message : String(e);
}
